import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Shield as AuditIcon,
  FileDownload as DownloadIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';

import { auditLogsApi } from '../api/services';
import type { AuditLogEntry } from '../types/models';

// ---------------------------------------------------------------------------
// AuditLogPage — Phase 3 task 3.9.
// Executive-only page exposing the immutable audit log with date range,
// action / entity filters, and a CSV export. Entries come from the
// AuditLoggingInterceptor automatically for the audited entity types, plus
// any explicit writes from individual controllers.
// ---------------------------------------------------------------------------

function formatTs(ts: string): string {
  try { return format(new Date(ts), 'd MMM yyyy HH:mm:ss'); } catch { return ts; }
}

export default function AuditLogPage() {
  const today = format(new Date(), 'yyyy-MM-dd');
  const sevenDaysAgo = format(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd');
  const [from, setFrom] = useState(sevenDaysAgo);
  const [to, setTo] = useState(today);
  const [actionFilter, setActionFilter] = useState('');
  const [entityFilter, setEntityFilter] = useState('');
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await auditLogsApi.getAll({
        from: new Date(from).toISOString(),
        to: new Date(new Date(to).getTime() + 24 * 60 * 60 * 1000 - 1).toISOString(),
        action: actionFilter || undefined,
        entityType: entityFilter || undefined,
        limit: 500,
      });
      setLogs(data);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Could not load audit log.');
    } finally {
      setLoading(false);
    }
  }, [from, to, actionFilter, entityFilter]);

  useEffect(() => { load(); }, [load]);

  const entityTypes = useMemo(
    () => Array.from(new Set(logs.map((l) => l.entityType).filter(Boolean))) as string[],
    [logs],
  );
  const actions = useMemo(
    () => Array.from(new Set(logs.map((l) => l.action).filter(Boolean))) as string[],
    [logs],
  );

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await auditLogsApi.exportCsv({
        from: new Date(from).toISOString(),
        to: new Date(new Date(to).getTime() + 24 * 60 * 60 * 1000 - 1).toISOString(),
        action: actionFilter || undefined,
        entityType: entityFilter || undefined,
      });
      const blob = res.data as Blob;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-log-${from}-${to}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 0);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Could not export CSV.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 2, md: 3 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <AuditIcon sx={{ color: 'primary.main', fontSize: 28 }} />
          <Typography variant="h4">Audit log</Typography>
        </Stack>
        <Button
          variant="outlined"
          startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}
          disabled={exporting || logs.length === 0}
          onClick={handleExport}
        >
          Export CSV
        </Button>
      </Stack>
      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
        Immutable record of significant writes across work items, plans, decisions, drivers, projects, KPIs, and out-of-office windows. Exportable for compliance.
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <TextField
              label="From"
              type="date"
              size="small"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              label="To"
              type="date"
              size="small"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <Select
              size="small"
              displayEmpty
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              sx={{ minWidth: 220 }}
            >
              <MenuItem value=""><em>All actions</em></MenuItem>
              {actions.map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
            </Select>
            <Select
              size="small"
              displayEmpty
              value={entityFilter}
              onChange={(e) => setEntityFilter(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value=""><em>All entities</em></MenuItem>
              {entityTypes.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>
          </Stack>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={24} /></Box>}

      {!loading && logs.length === 0 && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" color="text.secondary">No audit entries in this window.</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Widen the date range or change the filters to see more.
            </Typography>
          </CardContent>
        </Card>
      )}

      {!loading && logs.length > 0 && (
        <TableContainer component={Card}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>When</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Entity</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Detail</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map((l) => (
                <TableRow key={l.id}>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                    {formatTs(l.timestamp)}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={l.action} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                      {l.entityType}
                      {l.entityId ? ` · ${l.entityId.slice(0, 8)}` : ''}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 420 }}>
                    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                      {l.detail ?? '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                      {l.userId.slice(0, 12)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
